import { useEffect, useState } from "react";
import OverviewMain from "./Overview/OverviewMain";
import { useNavigate, useParams } from "react-router-dom";
import RotatingLogo from "../../Loaders/RotatingLogo";
import { useLanguage } from "../../Translations/LenguageContext";
import { BsOpenai } from "react-icons/bs";
import PromptSimLoader from "../../Loaders/PromptSimLoader";
import GeoPromptsMain from "./GeoPromptsMain";
import GeoActionsMain from "./Actions/GeoActionsMain";
import { MdSettings } from "react-icons/md";
import GeoSettingsModal from "./GeoSettingsModal";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const GeoOverview = ({
    account_id,
    org_id,
    geo_id,
    fetchGeoSettings,
    geoSettings
}) => {
    const { t } = useLanguage()
    const { geo_page, overview_page } = useParams()
    const navigate = useNavigate()
    const [promptsRunStatus, setPromptsRunStatus] = useState("")
    const [engines, setEngines] = useState([])
    const [count, setCount] = useState(0)
    const [loadingPrompts, setLoadingPrompts] = useState(false)
    const [prompts, setPrompts] = useState([])
    const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [openSettingsModal, setOpenSettingsModal] = useState(false)

    const navigatePage = (page) => {
        navigate(`/console/organizations/${org_id}/geo/${geo_id}/geo-overview/${page}`);
    }

    const pollStatus = async () => {
        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompts_simulation_run_status`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.data.prompts_run_status == "PENDING") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/simulation-prompts`)
            }

            if (result.data.prompts_run_status) {
                setPromptsRunStatus(result.data.prompts_run_status)
            }

            if (result.data.engines) {
                setEngines(result.data.engines)
            }

            if (result.data.simulation_count) {
                setCount(Number(result.data.simulation_count))
            }

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        }
    };

    const getPrompts = async () => {
        setLoadingPrompts(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompts`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setPrompts(result.prompts)

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setTimeout(() => {
                setLoadingPrompts(false);
            }, 500);
        }
    };

    useEffect(() => {
        pollStatus();
        if (promptsRunStatus == "QUEUED") {
            getPrompts()
        }
    }, [promptsRunStatus, account_id, org_id, geo_id]);

    useEffect(() => {
        if (!prompts.length) return;

        setIsVisible(true);

        const nextPromptTimeout = setTimeout(() => {
            setIsVisible(false);
        }, 2200);

        const transitionTimeout = setTimeout(() => {
            setCurrentPromptIndex(
                (prev) => (prev + 1) % prompts.length
            );
        }, 4000);

        return () => {
            clearTimeout(nextPromptTimeout);
            clearTimeout(transitionTimeout);
        };
    }, [currentPromptIndex, prompts]);

    useEffect(() => {
        const shouldPoll = ["QUEUED", "RUNNING"].includes(promptsRunStatus);

        if (!shouldPoll) return;

        const interval = setInterval(() => {
            pollStatus();
        }, 5000);

        return () => clearInterval(interval);
    }, [promptsRunStatus, account_id, org_id, geo_id]);


    if (!promptsRunStatus) {
        return (
            <div className="geo_context_cont fade-in">
                <RotatingLogo />
            </div>
        )
    }

    if (promptsRunStatus == "QUEUED") {
        return (
            <div className="prompts_simulation_run_queued_cont">
                <div className="gen_topic_animation_container fade-in">
                    <div className="gen_topic_animation_cont">
                        <div className="gen_topic_cont_queued">
                            <p className="add_new_source_cont_title fade-in">
                                {t.geo.running_sim_title}
                            </p>
                            <p className="add_new_source_cont_desc fade-in">
                                {t.geo.running_sim_desc}
                            </p>

                            <div className="prompts_simulation_run_queued_cont_prompts">
                                {loadingPrompts || prompts.length == 0 ? (
                                    <PromptSimLoader />
                                ) : (
                                    <div
                                        key={currentPromptIndex}
                                        className={`queued_prompt_cont ${isVisible ? "prompt-visible" : "prompt-hidden"
                                            }`}
                                    >
                                        <p className="queued_prompt_cont_p fade-in">
                                            {prompts[currentPromptIndex]?.prompt}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="prompts_simulation_run_queued_icons_cont">
                                {engines.map((engine) => (
                                    <div
                                        className="prompts_run_engine_icons"
                                        style={
                                            engine !== "chatgpt"
                                                ? { backgroundImage: `url('/logos/${engine}.png')` }
                                                : undefined
                                        }
                                    >
                                        {engine === "chatgpt" && <span><BsOpenai /></span>}
                                    </div>
                                ))}
                                <p>x {count}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (promptsRunStatus == "COMPLETED" || promptsRunStatus == "RUNNING") {
        return (
            <div className="geo_overview_cont">
                <div className="geo_overview_header">
                    <div className="geo_overview_header_inner_btn_cont">
                        <button className={`geo_overview_header_btn ${overview_page == "overview" && "geo_overview_header_btn_selected"}`} onClick={() => navigatePage("overview")}>{t.geo.overview}</button>
                        <button className={`geo_overview_header_btn ${overview_page == "prompts" && "geo_overview_header_btn_selected"}`} onClick={() => navigatePage("prompts")}>{t.geo.prompts}</button>
                        <button className={`geo_overview_header_btn ${overview_page == "actions" && "geo_overview_header_btn_selected"}`} onClick={() => navigatePage("actions")}>{t.geo.actions}</button>
                    </div>
                    <button className={`geo_overview_header_btn ${overview_page == "settings" && "geo_overview_header_btn_selected"}`} onClick={() => setOpenSettingsModal(true)}><MdSettings /></button>
                </div>

                {overview_page == "overview" ? (
                    <OverviewMain
                        account_id={account_id}
                        org_id={org_id}
                        geo_id={geo_id}
                    />
                ) : overview_page == "prompts" ? (
                    <GeoPromptsMain
                        account_id={account_id}
                        org_id={org_id}
                        geo_id={geo_id}
                        getPrompts={getPrompts}
                        prompts={prompts}
                        setLoadingPrompts={setLoadingPrompts}
                        loadingPrompts={loadingPrompts}
                    />
                ) : overview_page == "actions" ? (
                    <GeoActionsMain
                        account_id={account_id}
                        org_id={org_id}
                        geo_id={geo_id}
                    />
                ) : (
                    <p>This page does not exist...</p>
                )}

                <GeoSettingsModal
                    account_id={account_id}
                    org_id={org_id}
                    geo_id={geo_id}
                    show={openSettingsModal}
                    handleClose={() => setOpenSettingsModal(false)}
                    fetchGeoSettings={fetchGeoSettings}
                    geoSettings={geoSettings}
                />
            </div>
        )
    }
}

export default GeoOverview